import { SmartLink } from "@selector/shared/components";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  DialogComponent,
  Icons,
  Item,
  LoadingSpinner,
  MenuOptionItem,
  MenuOptionItemIntent,
  MenuTriggerButton,
  Popover,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { useError } from "@/app-utils/graphql/useError.ts";
import { getPlaybookEventResourcesSummary } from "@/app-utils/playbook.ts";
import { pluralizeWord } from "@/app-utils/string.ts";
import { type EventResource, type PlaybookEvent, type ProjectFile } from "@/graphql.ts";
import { getFilesApplicableToGenomeViewer } from "@/modules/files/utils/utils.ts";
import { getProjectFilesDataForSendingToGenomeViewer } from "@/redux/modules/files.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { EVENT_RESOURCE_TYPES } from "@/settings/settings.ts";
import type { IAgGridCellProps } from "@/ui-modules/core/grid/cell-base-interfaces.ts";
import ListCellContent from "@/ui-modules/core/grid/cells/core/ListCellContent.tsx";
import { TableCell } from "@/ui-modules/core/grid/cells/core/TableCell.tsx";
import BamFileValidationDialog from "@/ui-modules/files/BamFileValidationDialog.tsx";
import { useGetPlaybookEventOutputsCardLazyQuery } from "@/ui-modules/playbooks/events/graphql/event-queries.gql.ts";
import { PlaybookEventResource } from "@/ui-modules/playbooks/events/PlaybookEventResource.tsx";

type PlaybookEventGridRow = Pick<PlaybookEvent, "id" | "job" | "alias" | "results" | "playbook">;

interface ResourceItem {
  name: string;
  link?: string;
}

export const EventResourceCell = (props: IAgGridCellProps) => {
  const { value, colDef, context } = props;
  const data = props.data as PlaybookEventGridRow | undefined;
  const { id, job, alias, results, playbook } = data ?? {};
  const name = playbook?.name;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay();

  const [loadDetails, { data: detailData, loading: detailLoading, error: detailError }] =
    useGetPlaybookEventOutputsCardLazyQuery();

  void useError(detailError);

  useEffect(() => {
    if (state.isOpen && id) {
      void loadDetails({ variables: { eventId: id } });
    }
  }, [state.isOpen, id, loadDetails]);

  const [fileResources, fileResourcesCount] = useMemo(() => {
    const files = (results ?? [])
      .map((r) => r?.file)
      .filter((file): file is NonNullable<typeof file> => file != null && !file.hidden);
    return [files, files.length];
  }, [results]);

  const restResources = useMemo(() => (results ?? []).length - fileResourcesCount, [results, fileResourcesCount]);

  const resourceItems = useMemo<ResourceItem[]>(() => {
    const resourceItems: ResourceItem[] = fileResources.map(({ fileName, id, deleted }) => ({
      name: fileName ?? "Unnamed file",
      link: !deleted ? `/content/files?file=${id}` : undefined,
    }));
    if (restResources > 0) {
      resourceItems.push({ name: `${restResources} unknown ${pluralize("resource", restResources)}` });
    }
    return resourceItems;
  }, [fileResources, restResources]);

  const items = resourceItems.map(({ name, link }) => ({
    value: link ? (
      <SmartLink to={link} style={{ width: "auto", display: "inline" }}>
        {name}
      </SmartLink>
    ) : (
      name
    ),
    tooltip: name,
  }));

  const detailResources = useMemo(() => {
    const output = detailData?.playbook?.event?.results?.filter((r) => r != null);
    return Object.entries(getPlaybookEventResourcesSummary(output as EventResource[])).flatMap(
      ([_, eventResource]) => eventResource,
    );
  }, [detailData?.playbook?.event?.results]);

  const detailFileResources = useMemo(
    () =>
      (detailResources ?? [])
        .flatMap((output) => output?.file)
        .filter((file): file is ProjectFile => file != null && !file.hidden && !file.deleted),
    [detailResources],
  );

  if (!value || !id) {
    return null;
  }

  // job results case
  if (job) {
    if (job.resultCount) {
      const value = `${job.resultCount} ${pluralizeWord("BLAST result", job.resultCount)}`;
      return (
        <TableCell>
          <SmartLink to={`/job/${job.id}/results`} tooltip={value}>
            {value}
          </SmartLink>
        </TableCell>
      );
    }

    return null;
  }

  if (!results) return null;

  return (
    <TableCell
      alternativeButton={
        resourceItems.length > 0 ? (
          <>
            <TriggerButton
              label=""
              icon="more_vert"
              {...triggerProps}
              size={TriggerButtonSize.Small}
              ref={triggerRef}
            />
            <Popover state={state} triggerRef={triggerRef} {...overlayProps} offset={8}>
              <DialogComponent
                closeFn={state.close}
                width={400}
                title={alias ?? name ?? "Resources"}
                actions={
                  detailFileResources.length > 0 ? [<SendToGenomeViewer key="send" files={detailFileResources} />] : []
                }
              >
                {detailLoading ? (
                  <LoadingSpinner />
                ) : (
                  <VStack>
                    {detailResources.map((resource, i) => (
                      <PlaybookEventResource
                        key={i}
                        eventResource={resource}
                        resourceCategory={
                          resource.resourceType === EVENT_RESOURCE_TYPES.UNKNOWN
                            ? undefined
                            : (resource.resourceType ?? undefined)
                        }
                      />
                    ))}
                  </VStack>
                )}
              </DialogComponent>
            </Popover>
          </>
        ) : undefined
      }
    >
      <ListCellContent items={items} colDef={colDef} context={context} style={{ width: "100%", minWidth: 0 }} />
    </TableCell>
  );
};

const SendToGenomeViewer = ({ files }: { files: ProjectFile[] }) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const filesApplicableToGenomeViewer = useMemo(() => getFilesApplicableToGenomeViewer(files), [files]);
  const [projectFilesForGenomeViewer, setProjectFilesForGenomeViewer] = useState<ProjectFile[] | null>(null);

  if (filesApplicableToGenomeViewer.length === 0) return null;

  return (
    <>
      <MenuTriggerButton
        icon={"more_vert"}
        size={TriggerButtonSize.Small}
        label={""}
        onAction={(key) => {
          switch (String(key)) {
            case "file-menu-genomeviewer":
              setProjectFilesForGenomeViewer(
                dispatch(
                  getProjectFilesDataForSendingToGenomeViewer(filesApplicableToGenomeViewer.map((file) => file.id)),
                ),
              );
              break;
          }
        }}
      >
        <Item key="file-menu-genomeviewer">
          <MenuOptionItem
            title="Send All to Genome Viewer"
            intent={MenuOptionItemIntent.Primary}
            icon={<img src={Icons.Action.GenomeViewer} width={20} height={20} />}
          />
        </Item>
      </MenuTriggerButton>
      {projectFilesForGenomeViewer && (
        <BamFileValidationDialog
          key="file-bam-validation-dialog"
          projectFiles={projectFilesForGenomeViewer}
          onClose={() => {
            setProjectFilesForGenomeViewer(null);
          }}
        />
      )}
    </>
  );
};
